/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import braintree from 'braintree-web';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useGetConfigQuery } from 'renderer/features/purchase-page/services/purchase.endpoints';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { Country, State } from 'country-state-city';
import { CheckboxWithLabel } from 'formik-mui';
import StripeCreditCardForm from 'renderer/features/purchase-page/components/StripeCreditCardForm';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import PaymentMethodForm from 'renderer/features/purchase-page/components/PaymentMethodForm';
import BillingAddressForm from './BillingAddressForm';
import {
  useCreatePaymentMethodMutation,
  useEditPaymentMethodMutation,
} from '../services/paymentMethods.endpoints';
import {
  EditBillingAddressFormSchema,
  NewPaymentMethodFormSchema,
  PaymentMethod,
} from '../schemas/paymentMethods.schemas';

const initialForm = {
  newPaymentMethodToken: '',
  nameOnCard: '',
  streetAddress: '',
  locality: '',
  region: '',
  regionCode: '',
  postalCode: '',
  countryName: '',
  countryCode: '',
  numberValidity: '',
  cvvValidity: '',
  expirationDateValidity: '',
  addNewCard: true,
  provider: '',
};

interface SuccessListenerProps {
  isSuccess: boolean;
}

const SuccessListener: React.FC<SuccessListenerProps> = ({ isSuccess }) => {
  const { resetForm } = useFormikContext();
  useEffect(() => {
    if (isSuccess) resetForm();
  }, [isSuccess, resetForm]);
  return null;
};

interface ExistingCardListenerProps {
  mode: 'edit' | 'create';
  card?: PaymentMethod;
}

const ExistingCardListener: React.FC<ExistingCardListenerProps> = ({
  card,
  mode,
}) => {
  const { setFieldValue, resetForm } = useFormikContext<typeof initialForm>();

  useEffect(() => {
    if (mode === 'edit' && card) {
      const countryCode = Country.getAllCountries().find(
        ({ name }) => card.billingAddress.countryName === name,
      )?.isoCode;

      const regionCode = State.getStatesOfCountry(countryCode ?? '').find(
        (s) => s.name === card.billingAddress.region,
      )?.isoCode;

      setFieldValue('nameOnCard', card.billingAddress.nameOnCard);
      setFieldValue('streetAddress', card.billingAddress.streetAddress);
      setFieldValue('locality', card.billingAddress.locality);
      setFieldValue('postalCode', card.billingAddress.postalCode);
      setFieldValue('countryCode', countryCode ?? '');
      setFieldValue('regionCode', regionCode ?? '');
    }
  }, [card, mode, setFieldValue]);

  useEffect(() => {
    if (mode === 'create') resetForm();
  }, [mode, resetForm]);

  return null;
};

interface AddCardDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  userId: string;
  card?: PaymentMethod;
  provider?: 'stripe' | 'braintree'; // made optional
}

const AddCardDialog: React.FC<AddCardDialogProps> = ({
  open,
  onClose,
  mode,
  userId,
  card,
  provider,
}) => {
  const { data: config } = useGetConfigQuery();

  // NEW — internal provider when parent does NOT supply one
  const [internalProvider, setInternalProvider] = useState<
    'stripe' | 'braintree' | null
  >(null);

  // Compute the effective provider:
  const effectiveProvider =
    provider ??
    internalProvider ??
    (mode === 'edit' && card ? (card.stripeId ? 'stripe' : 'braintree') : null);

  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);

  const [
    createPaymentMethod,
    {
      isLoading: createLoading,
      error: createError,
      isSuccess: createSuccess,
      reset: resetCreate,
    },
  ] = useCreatePaymentMethodMutation();

  const [
    editPaymentMethod,
    {
      isLoading: editLoading,
      isSuccess: editSuccess,
      error: editError,
      reset: resetEdit,
    },
  ] = useEditPaymentMethodMutation();

  const isLoading = createLoading || editLoading;

  const [tokenError, setTokenError] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleClose = () => {
    setTokenError('');
    resetCreate();
    resetEdit();
    setInternalProvider(null);
    onClose();
  };

  useEffect(() => {
    if (open && !formSubmitted) {
      setTokenError('');
      resetCreate();
      resetEdit();
    }
  }, [open, formSubmitted, resetCreate, resetEdit]);

  useEffect(() => {
    if (createSuccess) {
      resetCreate();
      onClose();
    }
  }, [createSuccess, onClose, resetCreate]);

  useEffect(() => {
    if (editSuccess && !open) resetEdit();
  }, [editSuccess, open, resetEdit]);

  const submitCreateForm = async (values: typeof initialForm) => {
    setFormSubmitted(true);
    if (!effectiveProvider) return;

    if (effectiveProvider === 'stripe') {
      if (!stripe || !elements) return;
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;

      const { paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      await createPaymentMethod({
        provider: 'stripe',
        userId,
        token: paymentMethod?.id ?? '',
        billingAddress: {
          nameOnCard: values.nameOnCard,
          countryName: values.countryName,
          streetAddress: values.streetAddress,
          locality: values.locality,
          region: values.region,
          postalCode: values.postalCode,
        },
      });
    }

    if (effectiveProvider === 'braintree' && hostedFieldsInstance) {
      try {
        const { nonce } = await hostedFieldsInstance.tokenize();
        await createPaymentMethod({
          provider: 'braintree',
          userId,
          token: nonce,
          billingAddress: {
            nameOnCard: values.nameOnCard,
            countryName: values.countryName,
            streetAddress: values.streetAddress,
            locality: values.locality,
            region: values.region,
            postalCode: values.postalCode,
          },
        });
      } catch (err) {
        console.error(err);
        setTokenError(
          'There was an error submitting your card. Please try again.',
        );
      }
    }
  };

  const submitEditForm = async (values: typeof initialForm) => {
    setFormSubmitted(true);
    if (!card) return;

    const { addNewCard } = values;

    const cardProvider = card.stripeId ? 'stripe' : 'braintree';

    // EDIT BT CARD
    if (card.braintreeId && hostedFieldsInstance && addNewCard) {
      try {
        const { nonce } = await hostedFieldsInstance.tokenize();
        await editPaymentMethod({
          id: card._id,
          payload: {
            provider: 'braintree',
            userId,
            token: nonce,
            billingAddress: {
              nameOnCard: values.nameOnCard,
              countryName: values.countryName,
              streetAddress: values.streetAddress,
              locality: values.locality,
              region: values.region,
              postalCode: values.postalCode,
            },
          },
        });
      } catch (err) {
        setTokenError('There was an error updating the card details.');
      }
      return;
    }

    // EDIT STRIPE CARD
    if (card.stripeId && stripe && elements && addNewCard) {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) return;

      const { paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      await editPaymentMethod({
        id: card._id,
        payload: {
          provider: 'stripe',
          userId,
          token: paymentMethod?.id ?? '',
          default: true,
          billingAddress: {
            nameOnCard: values.nameOnCard,
            countryName: values.countryName,
            streetAddress: values.streetAddress,
            locality: values.locality,
            region: values.region,
            postalCode: values.postalCode,
          },
        },
      });
      return;
    }

    // Billing address only
    if (!addNewCard) {
      await editPaymentMethod({
        id: card._id,
        payload: {
          provider: cardProvider,
          userId,
          billingAddress: {
            nameOnCard: values.nameOnCard,
            countryName: values.countryName,
            streetAddress: values.streetAddress,
            locality: values.locality,
            region: values.region,
            postalCode: values.postalCode,
          },
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Formik
        initialValues={initialForm}
        validate={(values) => {
          const schema =
            mode === 'create' || values.addNewCard
              ? NewPaymentMethodFormSchema
              : EditBillingAddressFormSchema;

          return toValidator(schema)(values);
        }}
        onSubmit={(values) => {
          if (mode === 'create') submitCreateForm(values);
          else submitEditForm(values);
        }}
      >
        {({ values }) => (
          <Form>
            <SuccessListener isSuccess={createSuccess} />
            <ExistingCardListener mode={mode} card={card} />

            <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
              {mode === 'create' ? 'Add New Card' : 'Edit Payment Method'}
            </DialogTitle>

            {config && (
              <DialogContent>
                {tokenError && <Alert severity="error">{tokenError}</Alert>}
                {createError && (
                  <Alert severity="error">
                    {formatQueryError(createError)}
                  </Alert>
                )}
                {editError && (
                  <Alert severity="error">{formatQueryError(editError)}</Alert>
                )}
                {editSuccess && (
                  <Alert severity="success">
                    Successfully edited payment method!
                  </Alert>
                )}

                {mode === 'edit' && (
                  <Field
                    component={CheckboxWithLabel}
                    name="addNewCard"
                    type="checkbox"
                    Label={{ label: 'Edit Card Info' }}
                  />
                )}

                {/* PROVIDER SELECTOR - Only when provider is not passed & in create mode */}
                {mode === 'create' && !provider && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Choose Payment Method Type
                    </Typography>

                    <Button
                      variant={
                        effectiveProvider === 'stripe'
                          ? 'contained'
                          : 'outlined'
                      }
                      sx={{ mr: 2 }}
                      onClick={() => setInternalProvider('stripe')}
                    >
                      Stripe Card
                    </Button>

                    <Button
                      variant={
                        effectiveProvider === 'braintree'
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => setInternalProvider('braintree')}
                    >
                      Braintree Card
                    </Button>
                  </Box>
                )}

                {/* STRIPE PAYMENT FORM */}
                {effectiveProvider === 'stripe' &&
                  (mode === 'create' ||
                    (values.addNewCard && card?.stripeId)) && (
                    <StripeCreditCardForm />
                  )}

                {/* BRAINTREE PAYMENT FORM */}
                {effectiveProvider === 'braintree' &&
                  (mode === 'create' ||
                    (values.addNewCard && card?.braintreeId)) && (
                    <PaymentMethodForm
                      disabled={false}
                      clientToken={config.braintreeClientToken}
                      hostedFieldsInstance={hostedFieldsInstance}
                      setHostedFieldsInstance={setHostedFieldsInstance}
                    />
                  )}

                <Typography variant="h6" sx={{ mt: 2 }}>
                  Billing Address
                </Typography>

                <BillingAddressForm disabled={isLoading} />
              </DialogContent>
            )}

            {!config && (
              <DialogContent>
                <CircularProgress />
              </DialogContent>
            )}

            <DialogActions>
              <Button type="submit" variant="contained" disabled={isLoading}>
                Save
              </Button>
              <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddCardDialog;
